// THE PORTFOLIO ZONE (owner vrequest 2026-09-18).
//
// The Current Zone line shows where the MARKET is (the zone indicator's assignment). The owner
// also wants to see where the PORTFOLIO is: the strike of its short options — "which zone our
// short options are right now, a fancy way to say what strikes our shorts are". The two sit on
// one line, portfolio zone left, market zone right, divided (the owner swapped the order on
// 2026-09-21: "the starting point and then the ending point").
//
// Owner rulings (2026-09-18, on the formalized request):
//   - read on the FRONTEND from the saved APG positions (the same user-setting key the run-data
//     watch already snapshots) — Maven still sends ids only, this is display, not run input;
//   - the near-dated `ShortOption` leg ONLY, per side — the spread legs and the protective
//     options are other roles and are not "the shorts"; this is the APG's own definition
//     (adjustment-pricing-grid.component.ts, orionZoneAtm);
//   - both strikes when the call short and the put short differ ("6930 / 6910");
//   - N/A when the shorts are not available or expired.
//
// The strike is parsed from the leg's ticker. ⚠ An expired APG leg arrives with an EMPTY ticker,
// not a past date (CR-20260916-001, first smoke) — so an unparseable ticker counts as expired,
// which `isOptionExpired` already does (void ticker => expired).
//
// PURE FUNCTIONS, no Angular. The component owns the read and the refresh.

import {BeforePositionDto} from '../../adjustment-pricing-grid/model/BeforePositionDto';
import {parseOptionTicker} from '../../options-common/options.model';
import {isValidNumber, isVoid} from '../../utils';

/** The short strike per side, or null: no such leg, no ticker, expired. */
export interface MavenPortfolioZone {
    call: number | null;
    put: number | null;
}

export const NO_PORTFOLIO_ZONE: MavenPortfolioZone = {call: null, put: null};

/**
 * The saved positions (`apg.saved-positions.*`: one array of legs per side) to the short strike
 * per side. `isExpired` is the caller's clock (the option-chain service's, not the browser's).
 * A side whose ShortOption leg is missing, tickerless or expired reads null.
 */
export function readPortfolioZone(
    saved: BeforePositionDto[][] | null | undefined,
    isExpired: (ticker: string) => boolean
): MavenPortfolioZone {
    const result: MavenPortfolioZone = {call: null, put: null};
    if (!Array.isArray(saved)) {
        return result;
    }
    saved.forEach(side => {
        if (!Array.isArray(side)) {
            return;
        }
        const short = side.find(leg => leg?.role === 'ShortOption');
        if (!short || isVoid(short.ticker) || isExpired(short.ticker)) {
            return;
        }
        const parsed = parseOptionTicker(short.ticker);
        if (isVoid(parsed) || !isValidNumber(parsed.strike, true)) {
            return;
        }
        // The side is the LEG's type, not the array's slot: a one-sided saved state has one
        // array, and which side it is only the ticker says.
        if (parsed.type === 'Call') {
            result.call = parsed.strike;
        } else if (parsed.type === 'Put') {
            result.put = parsed.strike;
        }
    });
    return result;
}

/**
 * The number(s) for the line: one strike when both sides agree (or only one side has one),
 * "call / put" when they differ, N/A when neither side has a short.
 */
export function formatPortfolioZone(zone: MavenPortfolioZone | null | undefined): string {
    const call = zone?.call ?? null;
    const put = zone?.put ?? null;
    if (call === null && put === null) {
        return 'N/A';
    }
    if (call === null) {
        return '' + put;
    }
    if (put === null || put === call) {
        return '' + call;
    }
    return `${call} / ${put}`;
}

// The keys the shorts can move under (saved-positions.service.ts / cashflow-strategy-templates
// .service.ts): the positions themselves, keyed by portfolio + the last-used template's
// underlying and strategy; and the templates index, whose last-used entry decides that key.
// The Package Comparison's keys (`cpg.saved-positions.left/right...`) are another root and never
// match — they are not this portfolio's book.
const SAVED_POSITIONS_ROOT = 'apg.saved-positions.';
const TEMPLATES_ROOT = 'apg.templates';

/** Does a settings echo under this key mean the portfolio's short strikes may have moved? */
export function isPortfolioZoneEcho(key: string, portfolioId: string): boolean {
    if (isVoid(key)) {
        return false;
    }
    return key.startsWith(SAVED_POSITIONS_ROOT + portfolioId + '.') || key.startsWith(TEMPLATES_ROOT);
}

export function hasPortfolioZone(zone: MavenPortfolioZone | null | undefined): boolean {
    return (zone?.call ?? null) !== null || (zone?.put ?? null) !== null;
}
